import { useGetCurrentUserQuery } from '../../../api/user.api';
import { useSelector } from 'react-redux';
import { selectAccount } from '../../../redux/slices/accountSlice';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import type { Asset } from '../../../api/models/asset.model';
import type { Contract } from '../../../api/models/contract.model';
import type { RequestType } from './RequestTypesDefinition';
import { useMemo } from 'react';
import * as userRoleHook from '../../../hooks/useUserAccountRole.ts';
import type { CsrRequestPayload } from '../../../api/csrRequest.api.ts';
import { selectIsPrivateLabel } from '../../../redux/slices/uiSettingsSlice.ts';
interface RequestModalProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (data: CsrRequestPayload) => void;
	selectedRecords: (Contract | Asset)[];
	requestTypes: RequestType[];
	submitDisabled?: boolean;
}

const RequestModal: React.FC<RequestModalProps> = ({
	open,
	onClose,
	onSubmit,
	selectedRecords,
	requestTypes,
	submitDisabled = false,
}) => {
	const { data: user } = useGetCurrentUserQuery();
	const selectedAccount = useSelector(selectAccount);
	const { role, isLoading } = userRoleHook.useUserAccountRole();
	const theme = useTheme();
	const fullscreen = useMediaQuery(theme.breakpoints.down('md'));
	const [requestData, setRequestData] = useState<any>({});

	// Only set requestType when data is loaded
	const [message, setMessage] = useState('');

	// Only filter request types when not loading
	const filteredRequestTypes = useMemo(() => {
		if (isLoading) return [];
		if (!role || !role.permissionLevel) return requestTypes;
		const { permissionLevel, roleType } = role;

		// only VIEW role is limited to general support
		// VIEW_AND_EDIT and ADMIN can see all request types
		if (permissionLevel === 'VIEW') {
			//if int view/sales rep, cannot make a request ticket
			if (roleType === 'INTERNAL_USER') {
				return [];
			}
			return requestTypes.filter((rt) => rt.value === 'generalSupport');
		}
		return requestTypes;
	}, [role, requestTypes, isLoading]);

	const [requestType, setRequestType] = useState(filteredRequestTypes[0]);

	// Update requestType when filteredRequestTypes changes
	React.useEffect(() => {
		if (!isLoading && filteredRequestTypes.length > 0) {
			setRequestType(filteredRequestTypes[0]);
		}
	}, [filteredRequestTypes, isLoading]);

	const handleClose = () => {
		setRequestType(filteredRequestTypes[0]);
		setMessage('');
		setRequestData({});
		onClose();
	};

	const handleSubmit = () => {
		const contactEmail = user?.emailAddress || '';
		const accountName = selectedAccount?.accountName || '';
		const payload: CsrRequestPayload = {
			requestType: requestType.value,
			message,
			entityData: {
				selectedRecords,
				requestData: Array.isArray(requestData) ? requestData : [requestData],
			},
			contactEmail,
			accountName,
		};
		onSubmit(payload);
	};

	const getTypeComponent = () => {
		return (
			requestType?.component && (
				<requestType.component onChange={setRequestData} selectedRecords={selectedRecords} />
			)
		);
	};

	const isPrivateLabel = useSelector(selectIsPrivateLabel);

	const requestNote = isPrivateLabel
		? `A notice sent to Lessor via this action constitutes a REQUEST only. Nothing on
										this portal overrides the terms of your lease contract. Lessor may accept or
										reject any request made by you in accordance with the terms of your lease
										contract or otherwise in its discretion.`
		: `A notice sent to Huntington Bank via this action constitutes a REQUEST only.
										Nothing on this circadia.huntington.com portal overrides the terms of your lease
										contract. Huntington Bank may accept or reject any request made by you in
										accordance with the terms of your lease contract or otherwise in its discretion.`;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			fullScreen={fullscreen}
			data-testid="request-modal"
		>
			<DialogTitle
				sx={{
					backgroundColor: (theme) => theme.palette.surface.tertiary,
					pr: 5,
					position: 'relative',
				}}
			>
				<span>Submit a Request</span>
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{
						position: 'absolute',
						top: '50%',
						right: 16,
						transform: 'translateY(-50%)',
						border: '2px solid',
						borderColor: (theme) => theme.palette.brand.border,
						color: (theme) => theme.palette.brand.border,
						borderRadius: '50%',
						p: 0.5,
						backgroundColor: 'background.paper',
						'&:hover': {
							backgroundColor: 'grey.100',
						},
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{isLoading ? (
					<Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
						<Typography variant="body2" color="text.secondary">
							Loading...
						</Typography>
					</Box>
				) : selectedRecords.length === 0 ? (
					<Box sx={{ mt: 2 }}>
						<Typography variant="body2" color="error" data-testid="no-records-message">
							Please select at least one record to submit a request.
						</Typography>
					</Box>
				) : (
					<>
						<Typography
							data-testid="selected-records-text"
							variant="body2"
							mt={2}
							sx={{ color: (theme) => theme.palette.brand.primary }}
						>
							{selectedRecords.length} records selected.
						</Typography>
						<Box sx={{ width: '50%' }}>
							<FormControl fullWidth margin="normal">
								<InputLabel id="request-type-label">Request Type</InputLabel>
								<Select
									labelId="request-type-label"
									value={requestType?.value}
									label="Request Type"
									onChange={(e) =>
										setRequestType(
											filteredRequestTypes.find((type) => type.value === e.target.value)!
										)
									}
									data-testid="request-type-select"
									inputProps={{ 'aria-label': 'Request Type' }}
								>
									{filteredRequestTypes.map((type) => (
										<MenuItem
											key={type.value}
											value={type.value}
											data-testid={`request-type-${type.value}`}
										>
											{type.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						{getTypeComponent()}
						<Box sx={{ mt: 2 }}>
							<TextField
								label="Message"
								fullWidth
								multiline
								minRows={4}
								focused
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								slotProps={{ htmlInput: { 'data-testid': 'request-message' } }}
							/>
							<Stack
								direction="row"
								alignItems="flex-start"
								spacing={1}
								mt={2}
								mb={2}
								sx={{
									border: '1px solid',
									borderRadius: 2,
									p: 2,
									alignItems: 'flex-start',
								}}
							>
								<InfoOutlinedIcon fontSize="small" sx={{ mt: '2px', mr: 1 }} />
								<Box>
									<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
										Note
									</Typography>
									<Typography variant="body2" color="text.secondary" data-testid="request-note">
										{requestNote}
									</Typography>
								</Box>
							</Stack>
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button
					type="submit"
					data-testid="request-submit-button"
					form="request-form"
					variant="contained"
					color="primary"
					fullWidth
					onClick={handleSubmit}
					disabled={selectedRecords.length === 0 || isLoading || submitDisabled}
				>
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default RequestModal;
